import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Collapse, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Paper, Badge as MuiBadge } from '@mui/material';
import { Dashboard, People, ShoppingCart, ContactSupport, LocalShipping, Task, Settings, Inventory, Badge, AccountCircle, TwoWheeler, AccountBalance, AccountBalanceWallet, Paid, CallMerge, History, AssignmentReturn, EventAvailable, EventRepeat, Storefront, ExpandLess, ExpandMore, Fingerprint, FactCheck, FindInPage, Warehouse, AssignmentTurnedIn, Phone, EditNote, Savings, ReceiptLong, Store, Category, RequestQuote, Assessment, Handshake, Chat, PersonSearch, Campaign } from '@mui/icons-material';
import { useAuth, isAdminLevel } from '../AuthContext';
import { supabase } from '../supabaseClient';

// How often to re-poll the pending-action badge counts - matches Navbar's
// notification bell poll interval.
const BADGE_POLL_MS = 20000;

const todayDateStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const NavLink = ({ to, icon, label, active, onClick, indent, badgeCount }) => (
  <ListItemButton
    component={Link}
    to={to}
    selected={active}
    onClick={onClick}
    sx={{
      mx: 1,
      my: 0.25,
      pl: indent === 2 ? 6 : indent ? 4 : 2,
      borderRadius: 2,
      transition: 'transform 0.18s ease, background-color 0.18s ease, padding-right 0.18s ease',
      '&:hover': {
        bgcolor: 'action.hover',
        transform: 'translateX(4px)',
        pr: 3,
      },
      '&.Mui-selected': {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { bgcolor: 'primary.dark', transform: 'translateX(-4px)' },
        '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
      },
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: indent ? 36 : 56,
        transition: 'transform 0.18s ease, color 0.18s ease',
        '.MuiListItemButton-root:hover &': { transform: 'scale(1.15)' },
      }}
    >
      {badgeCount > 0 ? (
        <MuiBadge badgeContent={badgeCount} color="error" max={99}>
          {icon}
        </MuiBadge>
      ) : (
        icon
      )}
    </ListItemIcon>
    <ListItemText primary={label} />
  </ListItemButton>
);

const Sidebar = ({ mobileOpen, onClose }) => {
  const { currentUser, hasPageAccess, permissionsLoaded } = useAuth();
  const { pathname } = useLocation();
  // Defaults closed during the one-time post-login permissions fetch -
  // briefly hiding a link a role does have is far safer than briefly
  // showing one it doesn't (e.g. HR seeing the sales pipeline for a beat).
  // These links reappear correctly as soon as permissionsLoaded flips.
  const canSeePage = (pageKey) => permissionsLoaded && hasPageAccess(pageKey, 'view');
  const isHr = currentUser?.roleSlug === 'hr';
  // Plain 'admin' (not super_admin) no longer gets the Best Trade section at
  // all - it's scoped down to Tasks/Candidates/User Permissions oversight.
  const isRestrictedAdmin = currentUser?.roleSlug === 'admin';
  const isAdminLevelUser = isAdminLevel(currentUser?.roleSlug);
  const isTasksOnly = ['developer', 'marketing'].includes(currentUser?.roleSlug);
  const isWarehouseOnly = currentUser?.roleSlug === 'warehouse';
  const isDeliveryOnly = currentUser?.roleSlug === 'delivery_rep';
  const isOutsideSales = currentUser?.roleSlug === 'outside_sales';
  const isSalesManager = currentUser?.roleSlug === 'sales_manager';
  const isSalesRep = currentUser?.roleSlug === 'sales_rep';
  const isTeamLeader = currentUser?.roleSlug === 'team_leader';
  const isAccountsManager = currentUser?.roleSlug === 'accounts_manager';
  const isSuperAdmin = currentUser?.roleSlug === 'super_admin';
  const isAdministrative = currentUser?.roleSlug === 'administrative';
  // These used to also check a hardcoded role list alongside canSeePage;
  // now that every seed row is calibrated to match, canSeePage alone is
  // the single source of truth - which means a brand-new role created in
  // "المستخدمين والصلاحيات" gets correct sidebar visibility purely from
  // its page_permissions rows, with no code change needed here.
  const showReportsCenter = canSeePage('/reports/sales');
  const showAdsStats = canSeePage('/ads-stats');
  const canSeeReturns = canSeePage('/returns');
  const showInventoryAudit = canSeePage('/inventory-audit');
  const showWarehouseDelivery = canSeePage('/warehouse-delivery');
  const showApprovedAdjustments = canSeePage('/inventory-approvals');
  const canSeeAttendanceDashboard = canSeePage('/attendance-dashboard');

  // Pending-action badges next to a link: super_admin sees جرد/إضافة
  // requests waiting for approval, accounts_manager sees approved ones
  // waiting for review, sales_manager sees prepared orders waiting for a
  // delivery rep to be assigned.
  const [pendingInventoryApprovals, setPendingInventoryApprovals] = useState(0);
  const [unreviewedApprovedAdjustments, setUnreviewedApprovedAdjustments] = useState(0);
  const [preparedOrdersAwaitingDelivery, setPreparedOrdersAwaitingDelivery] = useState(0);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('inventory_adjustments')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) console.error('Error fetching pending inventory approvals count:', error);
      else setPendingInventoryApprovals(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isAccountsManager) return;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('inventory_adjustments')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'approved')
        .eq('reviewed', false);
      if (error) console.error('Error fetching unreviewed approved adjustments count:', error);
      else setUnreviewedApprovedAdjustments(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isAccountsManager]);

  useEffect(() => {
    if (!isSalesManager) return;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'prepared');
      if (error) console.error('Error fetching prepared orders count:', error);
      else setPreparedOrdersAwaitingDelivery(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isSalesManager]);

  // متابعات اليوم: both sources FollowupsPage.js itself lists - clients a
  // manager assigned for a phone call, plus the rep's own due follow-up
  // dates. Candidates no longer count here; HR does not see the page.
  const [dueFollowupsCount, setDueFollowupsCount] = useState(0);
  useEffect(() => {
    if (!isSalesRep && !isOutsideSales) return undefined;
    const fetchCount = async () => {
      const [assigned, stageBased] = await Promise.all([
        supabase
          .from('client_followups')
          .select('id', { count: 'exact', head: true })
          .eq('assigned_to', currentUser.id)
          .eq('status', 'pending'),
        supabase
          .from('clients')
          .select('id', { count: 'exact', head: true })
          .eq('assigned_to', currentUser.id)
          .not('stage_action_date', 'is', null)
          .lte('stage_action_date', todayDateStr()),
      ]);
      if (assigned.error) console.error('Error fetching assigned followups count:', assigned.error);
      if (stageBased.error) console.error('Error fetching due followups count:', stageBased.error);
      setDueFollowupsCount((assigned.count || 0) + (stageBased.count || 0));
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isSalesRep, isOutsideSales, currentUser?.id]);

  // أوردرات اليوم / تحصيلات المناديب / المرتجعات: same accounts_reviewed /
  // accounts_settled_at fields AccountsPage.js and CollectionsPage.js
  // already use to drive their own review checkboxes.
  const [unreviewedOrdersCount, setUnreviewedOrdersCount] = useState(0);
  const [unsettledCollectionsCount, setUnsettledCollectionsCount] = useState(0);
  const [unreviewedReturnsCount, setUnreviewedReturnsCount] = useState(0);

  useEffect(() => {
    if (!isAccountsManager) return;
    const fetchCounts = async () => {
      const [ordersResult, collectionsResult, returnsResult] = await Promise.all([
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('accounts_reviewed', false),
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('collection_status', 'collected')
          .is('accounts_settled_at', null),
        supabase.from('return_batches').select('id', { count: 'exact', head: true }).eq('accounts_reviewed', false),
      ]);
      if (ordersResult.error) console.error('Error fetching unreviewed orders count:', ordersResult.error);
      else setUnreviewedOrdersCount(ordersResult.count || 0);
      if (collectionsResult.error) console.error('Error fetching unsettled collections count:', collectionsResult.error);
      else setUnsettledCollectionsCount(collectionsResult.count || 0);
      if (returnsResult.error) console.error('Error fetching unreviewed returns count:', returnsResult.error);
      else setUnreviewedReturnsCount(returnsResult.count || 0);
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isAccountsManager]);

  // سجل التعديلات: orders that were edited, cancelled or deleted after
  // accounts had already ticked them reviewed, and haven't been signed off
  // again yet. Same three roles that can open the page (see showOrderChanges
  // below) - 'admin' is excluded from this whole menu.
  const [pendingOrderChangesCount, setPendingOrderChangesCount] = useState(0);
  useEffect(() => {
    if (!isAccountsManager && !isSalesManager && !isSuperAdmin) return undefined;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('order_change_log')
        .select('id', { count: 'exact', head: true })
        .eq('reviewed', false);
      if (error) console.error('Error fetching pending order changes count:', error);
      else setPendingOrderChangesCount(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [isAccountsManager, isSalesManager, isSuperAdmin]);

  // Tasks: personal unread notifications linking to /tasks - covers both a
  // task newly assigned to this user (existing notifyUser call) and, for
  // admin/super_admin, any new task at all (notifyAdmins).
  const [unreadTaskNotifications, setUnreadTaskNotifications] = useState(0);
  useEffect(() => {
    if (!currentUser?.id) return;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', currentUser.id)
        .eq('is_read', false)
        .eq('link', '/tasks');
      if (error) console.error('Error fetching unread task notifications count:', error);
      else setUnreadTaskNotifications(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // شات: نفس نمط عداد التاسكات، لكن بـ `like` بدل `eq` لأن لينك كل محادثة
  // فيه معرفها بعد /chat?c= (راجع ChatPage.js/notifyUser في sendMessage).
  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  useEffect(() => {
    if (!currentUser?.id) return;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', currentUser.id)
        .eq('is_read', false)
        .like('link', '/chat?c=%');
      if (error) console.error('Error fetching unread chat count:', error);
      else setUnreadChatMessages(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`);

  // Every one of these used to also check a hardcoded role list alongside
  // canSeePage; each page_permissions seed is calibrated to match that old
  // list exactly, so canSeePage alone now fully determines visibility -
  // meaning a brand-new role created in "المستخدمين والصلاحيات" gets
  // correct sidebar links purely from its page_permissions rows, with no
  // code change here.
  const showClients = canSeePage('/clients');
  const showOrders = canSeePage('/orders');
  const showInquiries = canSeePage('/inquiries');
  const showDeals = canSeePage('/deals');
  const showProducts = canSeePage('/products');
  const showDelivery = canSeePage('/delivery');
  const showVisits = canSeePage('/visits');
  const showFollowups = canSeePage('/followups');
  const showAccountsToday = canSeePage('/accounts');
  const showOrderChanges = canSeePage('/order-changes');
  const showCollections = canSeePage('/accounts/collections');
  const showCollectionsReview = canSeePage('/accounts/collections-review');
  const showClientLedger = canSeePage('/accounts/client-ledger');
  const showReturns = canSeeReturns;
  const showCalls = isSalesManager || isAdminLevelUser;

  const bestTradeChildRoutes = ['/clients', '/orders', '/inquiries', '/deals', '/products', '/delivery', '/visits', '/followups', '/accounts', '/order-changes', '/accounts/collections', '/accounts/collections-review', '/accounts/client-ledger', '/returns', '/inventory-audit', '/inventory-approvals', '/warehouse-delivery', '/calls'];
  const hasBestTradeItems =
    !isRestrictedAdmin &&
    (showClients || showOrders || showInquiries || showDeals || showProducts || showDelivery || showVisits || showFollowups || showAccountsToday || showOrderChanges || showCollections || showCollectionsReview || showClientLedger || showReturns || showInventoryAudit || showApprovedAdjustments || showWarehouseDelivery || showCalls);
  const isInsideBestTrade = bestTradeChildRoutes.some((r) => isActive(r));

  const [bestTradeOpen, setBestTradeOpen] = useState(isInsideBestTrade);
  useEffect(() => {
    if (isInsideBestTrade) setBestTradeOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // "الحسابات" sub-group inside Best Trade - groups every accounts-facing
  // page (order review, collections, client ledger, change log, approved
  // stock adjustments) under one nested menu instead of a flat list.
  const showTreasury = canSeePage('/accounts/treasury');
  const showShippingPolicies = canSeePage('/accounts/shipping-policies');
  const showSuppliers = canSeePage('/accounts/suppliers');
  const showExpenseSettings = canSeePage('/accounts/expense-settings');
  const showCompanyExpenses = canSeePage('/accounts/company-expenses');
  const showExpenseReport = canSeePage('/accounts/expense-report');
  const showCommissions = canSeePage('/accounts/commissions');
  const showRepCommissions = canSeePage('/accounts/rep-commissions');
  const showPayroll = canSeePage('/accounts/payroll');
  const showTeamDeductions = canSeePage('/accounts/team-deductions');
  const hasAccountsGroupItems =
    showAccountsToday ||
    showOrderChanges ||
    showCollections ||
    showCollectionsReview ||
    showClientLedger ||
    showApprovedAdjustments ||
    showTreasury ||
    showShippingPolicies ||
    showSuppliers ||
    showExpenseSettings ||
    showCompanyExpenses ||
    showExpenseReport ||
    showCommissions ||
    showRepCommissions ||
    showPayroll ||
    showTeamDeductions;
  const accountsGroupRoutes = [
    '/accounts',
    '/order-changes',
    '/accounts/collections',
    '/accounts/collections-review',
    '/accounts/client-ledger',
    '/inventory-approvals',
    '/accounts/treasury',
    '/accounts/shipping-policies',
    '/accounts/suppliers',
    '/accounts/expense-settings',
    '/accounts/company-expenses',
    '/accounts/expense-report',
    '/accounts/commissions',
    '/accounts/rep-commissions',
    '/accounts/payroll',
    '/accounts/team-deductions',
  ];
  const isInsideAccountsGroup = accountsGroupRoutes.some((r) => isActive(r));
  const [accountsGroupOpen, setAccountsGroupOpen] = useState(isInsideAccountsGroup);
  useEffect(() => {
    if (isInsideAccountsGroup) setAccountsGroupOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const navList = (onLinkClick) => (
    <List sx={{ pt: 1.5 }}>
      {!isHr && !isTasksOnly && !isWarehouseOnly && !isDeliveryOnly && !isTeamLeader && !isAdministrative && currentUser?.roleSlug !== 'customer_service' && (
        <NavLink to="/dashboard" icon={<Dashboard />} label="Dashboard" active={isActive('/dashboard')} onClick={onLinkClick} />
      )}
      {showReportsCenter && (
        <NavLink to="/reports" icon={<Assessment />} label="مركز التقارير" active={isActive('/reports')} onClick={onLinkClick} />
      )}
      {showAdsStats && (
        <NavLink to="/ads-stats" icon={<Campaign />} label="إحصائيات الإعلانات" active={isActive('/ads-stats')} onClick={onLinkClick} />
      )}
      {!isHr && !isWarehouseOnly && (
        <NavLink
          to="/tasks"
          icon={<Task />}
          label="Tasks"
          active={isActive('/tasks')}
          onClick={onLinkClick}
          badgeCount={unreadTaskNotifications}
        />
      )}
      <NavLink to="/chat" icon={<Chat />} label="الشات" active={isActive('/chat')} onClick={onLinkClick} badgeCount={unreadChatMessages} />

      {hasBestTradeItems && (
        <>
          <ListItemButton
            onClick={() => setBestTradeOpen((prev) => !prev)}
            sx={{
              mx: 1,
              my: 0.25,
              borderRadius: 2,
              transition: 'background-color 0.18s ease',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 56 }}>
              <Storefront />
            </ListItemIcon>
            <ListItemText primary="Best Trade" />
            {bestTradeOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={bestTradeOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {showClients && (
                <NavLink to="/clients" icon={<People />} label="Clients" active={isActive('/clients')} onClick={onLinkClick} indent />
              )}
              {showOrders && (
                <NavLink to="/orders" icon={<ShoppingCart />} label="أوردرات" active={isActive('/orders')} onClick={onLinkClick} indent />
              )}
              {showInquiries && (
                <NavLink to="/inquiries" icon={<ContactSupport />} label="استفسارات العملاء" active={isActive('/inquiries')} onClick={onLinkClick} indent />
              )}
              {showDeals && (
                <NavLink
                  to="/deals"
                  icon={<LocalShipping />}
                  label="تجهيز الأوردرات"
                  active={isActive('/deals')}
                  onClick={onLinkClick}
                  indent
                  badgeCount={preparedOrdersAwaitingDelivery}
                />
              )}
              {showProducts && (
                <NavLink to="/products" icon={<Inventory />} label="Products" active={isActive('/products')} onClick={onLinkClick} indent />
              )}
              {showDelivery && (
                <NavLink to="/delivery" icon={<TwoWheeler />} label="أوردراتي" active={isActive('/delivery')} onClick={onLinkClick} indent />
              )}
              {showVisits && (
                <NavLink to="/visits" icon={<EventAvailable />} label="زيارات اليوم" active={isActive('/visits')} onClick={onLinkClick} indent />
              )}
              {showFollowups && (
                <NavLink
                  to="/followups"
                  icon={<EventRepeat />}
                  label="متابعات اليوم"
                  active={isActive('/followups')}
                  onClick={onLinkClick}
                  indent
                  badgeCount={dueFollowupsCount}
                />
              )}
              {hasAccountsGroupItems && (
                <>
                  <ListItemButton
                    onClick={() => setAccountsGroupOpen((prev) => !prev)}
                    sx={{
                      mx: 1,
                      my: 0.25,
                      pl: 4,
                      borderRadius: 2,
                      transition: 'background-color 0.18s ease',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountBalance />
                    </ListItemIcon>
                    <ListItemText primary="الحسابات" />
                    {accountsGroupOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={accountsGroupOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {showAccountsToday && (
                        <NavLink
                          to="/accounts"
                          icon={<AccountBalance />}
                          label="أوردرات اليوم"
                          active={pathname === '/accounts'}
                          onClick={onLinkClick}
                          indent={2}
                          badgeCount={unreviewedOrdersCount}
                        />
                      )}
                      {showOrderChanges && (
                        <NavLink
                          to="/order-changes"
                          icon={<EditNote />}
                          label="سجل التعديلات"
                          active={isActive('/order-changes')}
                          onClick={onLinkClick}
                          indent={2}
                          badgeCount={pendingOrderChangesCount}
                        />
                      )}
                      {showCollections && (
                        <NavLink
                          to="/accounts/collections"
                          icon={<Paid />}
                          label="تحصيلات المناديب"
                          active={isActive('/accounts/collections')}
                          onClick={onLinkClick}
                          indent={2}
                          badgeCount={unsettledCollectionsCount}
                        />
                      )}
                      {showCollectionsReview && (
                        <NavLink
                          to="/accounts/collections-review"
                          icon={<FactCheck />}
                          label="مراجعة التحصيلات"
                          active={isActive('/accounts/collections-review')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showClientLedger && (
                        <NavLink
                          to="/accounts/client-ledger"
                          icon={<AccountBalanceWallet />}
                          label="حساب العملاء"
                          active={isActive('/accounts/client-ledger')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showApprovedAdjustments && (
                        <NavLink
                          to="/inventory-approvals"
                          icon={<AssignmentTurnedIn />}
                          label="آخر الجرد والإضافات المعتمدة"
                          active={isActive('/inventory-approvals')}
                          onClick={onLinkClick}
                          badgeCount={unreviewedApprovedAdjustments}
                          indent={2}
                        />
                      )}
                      {showTreasury && (
                        <NavLink
                          to="/accounts/treasury"
                          icon={<Savings />}
                          label="الخزينة"
                          active={isActive('/accounts/treasury')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showShippingPolicies && (
                        <NavLink
                          to="/accounts/shipping-policies"
                          icon={<ReceiptLong />}
                          label="بوالص الشحن"
                          active={isActive('/accounts/shipping-policies')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showSuppliers && (
                        <NavLink
                          to="/accounts/suppliers"
                          icon={<Store />}
                          label="حسابات الموردين"
                          active={isActive('/accounts/suppliers')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showExpenseSettings && (
                        <NavLink
                          to="/accounts/expense-settings"
                          icon={<Category />}
                          label="إعدادات المصاريف"
                          active={isActive('/accounts/expense-settings')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showCompanyExpenses && (
                        <NavLink
                          to="/accounts/company-expenses"
                          icon={<RequestQuote />}
                          label="مصاريف عامة"
                          active={isActive('/accounts/company-expenses')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showExpenseReport && (
                        <NavLink
                          to="/accounts/expense-report"
                          icon={<Assessment />}
                          label="تقرير المصاريف"
                          active={isActive('/accounts/expense-report')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showCommissions && (
                        <NavLink
                          to="/accounts/commissions"
                          icon={<Handshake />}
                          label="عمولات المعارض"
                          active={isActive('/accounts/commissions')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showRepCommissions && (
                        <NavLink
                          to="/accounts/rep-commissions"
                          icon={<Handshake />}
                          label="عمولات المندوبين"
                          active={isActive('/accounts/rep-commissions')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showPayroll && (
                        <NavLink
                          to="/accounts/payroll"
                          icon={<Paid />}
                          label="المرتبات"
                          active={isActive('/accounts/payroll')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                      {showTeamDeductions && (
                        <NavLink
                          to="/accounts/team-deductions"
                          icon={<Paid />}
                          label="خصومات الفريق"
                          active={isActive('/accounts/team-deductions')}
                          onClick={onLinkClick}
                          indent={2}
                        />
                      )}
                    </List>
                  </Collapse>
                </>
              )}
              {showReturns && (
                <NavLink
                  to="/returns"
                  icon={<AssignmentReturn />}
                  label="المرتجعات"
                  active={isActive('/returns')}
                  onClick={onLinkClick}
                  indent
                  badgeCount={unreviewedReturnsCount}
                />
              )}
              {showInventoryAudit && (
                <NavLink
                  to="/inventory-audit"
                  icon={<FindInPage />}
                  label="جرد وتعديل"
                  active={isActive('/inventory-audit')}
                  onClick={onLinkClick}
                  indent
                  badgeCount={pendingInventoryApprovals}
                />
              )}
              {showWarehouseDelivery && (
                <NavLink to="/warehouse-delivery" icon={<Warehouse />} label="استلام من المخزن" active={isActive('/warehouse-delivery')} onClick={onLinkClick} indent />
              )}
              {showCalls && (
                <NavLink to="/calls" icon={<Phone />} label="كل المكالمات" active={isActive('/calls')} onClick={onLinkClick} indent />
              )}
            </List>
          </Collapse>
        </>
      )}

      {canSeePage('/candidates') && (
        <NavLink to="/candidates" icon={<Badge />} label="Candidates" active={isActive('/candidates')} onClick={onLinkClick} />
      )}
      {canSeePage('/leads') && (
        <NavLink to="/leads" icon={<PersonSearch />} label="المرشحون" active={isActive('/leads')} onClick={onLinkClick} />
      )}
      <NavLink to="/attendance" icon={<Fingerprint />} label="تسجيل الحضور" active={isActive('/attendance')} onClick={onLinkClick} />
      {canSeeAttendanceDashboard && (
        <NavLink to="/attendance-dashboard" icon={<FactCheck />} label="سجل الحضور" active={isActive('/attendance-dashboard')} onClick={onLinkClick} />
      )}
      {canSeePage('/duplicates') && (
        <NavLink to="/duplicates" icon={<CallMerge />} label="Duplicates" active={isActive('/duplicates')} onClick={onLinkClick} />
      )}
      {canSeePage('/system-logs') && (
        <NavLink to="/system-logs" icon={<History />} label="System Logs" active={isActive('/system-logs')} onClick={onLinkClick} />
      )}
      {/* Deliberately hardcoded, not part of the dynamic page_permissions
          system: this is the page that manages that system (and every
          user's role/PIN), so it must never depend on a table it could
          itself misconfigure into a lockout. Only super_admin now - plain
          admin lost access here on request. */}
      {isSuperAdmin && (
        <NavLink to="/permissions" icon={<Settings />} label="User Permissions" active={isActive('/permissions')} onClick={onLinkClick} />
      )}
      <NavLink to="/profile" icon={<AccountCircle />} label="Profile" active={isActive('/profile')} onClick={onLinkClick} />
    </List>
  );

  return (
    <>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: 240,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: '1px 0 8px rgba(0,0,0,0.06)',
            },
          }}
        >
          {navList()}
        </Drawer>
      </Box>

      {mobileOpen && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
          }}
        >
          <Box
            onClick={onClose}
            sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)' }}
          />
          <Paper
            square
            elevation={16}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: 240,
              overflowY: 'auto',
            }}
          >
            {navList(onClose)}
          </Paper>
        </Box>
      )}
    </>
  );
};

export default Sidebar;
